import * as React from 'react'

import { Button, Loading } from '@factorialco/deprecated-design-system'
import {
  BulkCreateAuthenticationInvitationDocument,
  EmployeesEmployeeSelectorInput,
  NotInvitedEmployeesDocument
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useAllEmployeesWithAccess } from 'modules/employees/components/EmployeeCollection/Header/lib/useAllEmployeesWithAccess'
import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'

interface Props {
  isHidden: boolean
  onClose: () => void
}

const NotInvitedModal = ({ isHidden, onClose }: Props) => {
  const { company } = useCurrentSession()
  const [selection, setSelection] = React.useState<EmployeeSelectorV2MultiSelectState>({
    type: 'manual',
    ids: []
  })

  const [bulkCreateInvitation] = useMutation(BulkCreateAuthenticationInvitationDocument, {
    extractorFn: (data) => data.authenticationMutations.bulkCreateAuthenticationInvitation
  })

  // Load all employees for the selector to constrain the dropdown display
  const { employees, loading } = useAllEmployeesWithAccess(NotInvitedEmployeesDocument, {
    skip: isHidden,
    variables: { companyId: company.id }
  })

  if (isHidden) return null

  const handleClose = () => {
    setSelection({ type: 'manual', ids: [] })
    onClose()
  }

  const handleSubmit = async () => {
    track(trackingEvents.employee.employeesSendInviteFinish)

    let variables: {
      companyId: number
      employeeIds: number[]
      employeeSelection?: EmployeesEmployeeSelectorInput
    }

    if (selection.type === 'manual') {
      variables = {
        companyId: company.id,
        employeeIds: selection.ids
      }
    } else {
      // "Select all": delegate resolution to the backend via employeeSelection.
      // Inject accessStatus filter so the backend only considers "not_invited" employees,
      // since accessStatus is a forcedFilter not included in selection.filters.
      variables = {
        companyId: company.id,
        employeeIds: [],
        employeeSelection: {
          type: selection.type,
          excludedIds: selection.excludedIds,
          filters: {
            ...selection.filters,
            accessStatus: ['not_invited']
          }
        }
      }
    }

    const result = await bulkCreateInvitation({ variables })

    const handled = handleResult(result, () => {})

    if (handled.success) {
      handleClose()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving')
      })
    }
  }

  return (
    <Modal
      title={i18n.t('employees.not_invited_modal.title')}
      description={i18n.t('employees.not_invited_modal.description')}
      illustration="group"
      onSubmit={handleSubmit}
      onClose={handleClose}
      footer={<Button submit>{i18n.t('employees.not_invited_modal.submit')}</Button>}
    >
      {loading ? (
        <Loading />
      ) : (
        <EmployeeSelectorV2
          forcedFilters={{ employeeIds: employees.map((e) => e.id), accessStatus: ['not_invited'] }}
          onChange={setSelection}
          multiple
          options={{ disableSelectAll: false }}
        />
      )}
    </Modal>
  )
}

export default NotInvitedModal
